import { DropDownBox } from '@apply/components';
import DataSource from 'devextreme/data/data_source';
import { ExperiencePropertyType, ExperiencePropertyTypeOptions, ExperiencePropertyTypeCategory } from '@apply/constants';
import {
    useExperienceCommercialConsiderationAcknowledged,
    useExperiencePropertyTypeId,
    useExperienceTwoToFourUnitTypeId,
    useExperienceUnitCount,
} from '../../../../fields/ExperienceFields';

export interface PropertyTypeProps {
    experienceId: string | undefined;
    width?: string;
    required?: boolean;
    readOnly?: boolean;
}

export const PropertyType: React.FC<PropertyTypeProps> = ({ experienceId, required = false, readOnly = false }) => {
    const label = 'Property Type ';
    const [propertyType, set_propertyTypeId] = useExperiencePropertyTypeId(experienceId);
    const [, set_unitCount] = useExperienceUnitCount(experienceId);
    const [, set_commercialConsiderationAcknowledged] = useExperienceCommercialConsiderationAcknowledged(experienceId);
    const [, set_twoToFourUnitTypeId] = useExperienceTwoToFourUnitTypeId(experienceId);

    // Define the order you want
    const groupOrder = ['Typical Projects', 'Additional Projects'];

    // Build grouped items
    const items = ExperiencePropertyTypeOptions.map((o) => ({
        display: o.name,
        value: o.id,
        group: o.category === ExperiencePropertyTypeCategory.CoreOptions ? 'Typical Projects' : 'Additional Projects',
    }));

    // Build a grouped DataSource with custom group sorting
    const propertyTypeList = new DataSource({
        store: items,
        group: {
            selector: 'group',
            compare: (a: string, b: string) => groupOrder.indexOf(a) - groupOrder.indexOf(b),
        },
    } as any);

    const onPropertyTypeChange = (id) => {
        // First render: hydrate state, do NOT run logic
        if (propertyType === undefined) {
            set_propertyTypeId(id);
            return;
        }

        // Ignore re-selecting same value
        if (id === propertyType) {
            return;
        }

        // Update propertyType FIRST
        set_propertyTypeId(id);

        // Now run logic based on NEW value
        switch (id) {
            case ExperiencePropertyType.DetachedHouse:
            case ExperiencePropertyType.Townhouse:
            case ExperiencePropertyType.CondoUnit:
                set_unitCount(1);
                break;

            case ExperiencePropertyType.FivePlusMultifamily:
            case ExperiencePropertyType.TwoToFourUnitStructure:
                set_unitCount(undefined);
                break;

            case ExperiencePropertyType.ResidentialMixedUse:
            case ExperiencePropertyType.Commercial:
            default:
                set_unitCount(undefined);
                break;
        }

        if (id !== ExperiencePropertyType.Commercial) {
            set_commercialConsiderationAcknowledged(undefined);
        }

        if (id !== ExperiencePropertyType.TwoToFourUnitStructure) {
            set_twoToFourUnitTypeId(undefined);
        }
    };

    return (
        <DropDownBox
            id='experiencePropertyTypeId'
            value={propertyType}
            onChange={(value) => onPropertyTypeChange(value)}
            required={required}
            dataSource={propertyTypeList}
            displayExpr='display'
            valueExpr='value'
            label={label}
            readOnly={readOnly}
            grouped={true}
        />
    );
};
